import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Ban } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Spinner } from "@/components/common";

export function BlockDialog({ open, onOpenChange, userId, userName, onBlocked }) {
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) api.get("/blocks/reasons").then(({ data }) => setReasons(data.reasons)).catch(() => {}); }, [open]);

  const submit = async () => {
    if (!reason) return toast.error("Please choose a reason");
    setLoading(true);
    try {
      await api.post("/blocks", { user_id: userId, reason, details });
      toast.success(`You blocked ${userName || "this user"}`);
      onBlocked?.();
      onOpenChange(false);
      setReason(""); setDetails("");
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="block-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Ban className="h-4 w-4 text-destructive" />Block {userName || "this user"}?</DialogTitle>
          <DialogDescription>They won't be able to message or interact with you, and their listings will be hidden from you.</DialogDescription>
        </DialogHeader>
        <p className="text-sm font-medium">Why are you blocking this person?</p>
        <RadioGroup value={reason} onValueChange={setReason} className="space-y-1.5">
          {reasons.map((r) => (
            <label key={r} className="flex cursor-pointer items-center gap-2 rounded-lg border border-border p-2 text-sm has-[:checked]:border-primary has-[:checked]:bg-primary/5">
              <RadioGroupItem value={r} data-testid={`block-reason-${r}`} /> {r}
            </label>
          ))}
        </RadioGroup>
        <Textarea placeholder="Additional details (optional, private to Mtaa)" value={details} onChange={(e) => setDetails(e.target.value)} data-testid="block-details" />
        <DialogFooter className="flex-row gap-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} data-testid="block-cancel">Cancel</Button>
          <Button variant="destructive" className="flex-1" onClick={submit} disabled={loading} data-testid="block-confirm">
            {loading ? <Spinner className="text-white" /> : "Block user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
